<template>
  <div
    class="min-h-screen flex items-start justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4 py-10"
  >
    <!-- Loading State -->
    <div v-if="isLoading" class="max-w-lg w-full text-center mt-20">
      <div
        class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"
      ></div>
      <p class="text-gray-500 text-sm">Loading your preferences…</p>
    </div>

    <!-- Error State -->
    <div
      v-else-if="errorState"
      class="max-w-md w-full bg-white rounded-xl shadow-sm p-8 text-center mt-10"
    >
      <div
        class="mx-auto w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mb-4"
      >
        <svg
          class="w-7 h-7 text-red-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
      <h2 class="text-xl font-bold text-gray-800 mb-2">{{ errorTitle }}</h2>
      <p class="text-gray-500 text-sm leading-relaxed">{{ errorMessage }}</p>
      <p class="text-gray-400 text-xs mt-6">
        Need help?
        <a href="mailto:info@hova.ai" class="text-primary hover:underline"
          >Contact support</a
        >
      </p>
    </div>

    <!-- Preferences Page -->
    <div v-else class="max-w-lg w-full">
      <div class="bg-white rounded-xl shadow-sm overflow-hidden">
        <!-- Header -->
        <div
          class="bg-gradient-to-r from-primary to-purple-700 px-7 py-7 text-white"
        >
          <h1 class="text-xl font-bold flex items-center gap-2">
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            Email Preferences
          </h1>
          <p class="text-sm text-white/80 mt-1">
            Hello {{ userName }} — manage which emails you receive from
            HovaStore.
          </p>
        </div>

        <!-- Body -->
        <div class="px-7 py-6">
          <p class="text-gray-400 text-sm mb-5">
            Toggle the notifications you'd like to receive by email. Changes are
            saved automatically.
          </p>

          <!-- Preference Groups -->
          <div class="divide-y divide-gray-100">
            <div
              v-for="group in groups"
              :key="group.key"
              class="flex items-center justify-between py-4"
            >
              <div class="flex-1 pr-4">
                <div class="text-sm font-semibold text-gray-800">
                  {{ group.label }}
                </div>
                <div class="text-xs text-gray-400 mt-0.5 leading-relaxed">
                  {{ group.description }}
                </div>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  :checked="group.enabled"
                  class="sr-only peer"
                  @change="togglePreference(group.key, $event.target.checked)"
                />
                <div
                  class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"
                ></div>
              </label>
            </div>
          </div>

          <!-- Unsubscribe / Resubscribe All -->
          <div class="mt-5 text-center">
            <button
              v-if="allDisabled"
              @click="resubscribeAll"
              class="text-primary hover:text-purple-700 text-xs underline transition-colors"
            >
              Resubscribe to all emails
            </button>
            <button
              v-else
              @click="unsubscribeAll"
              class="text-red-500 hover:text-red-700 text-xs underline transition-colors"
            >
              Unsubscribe from all emails
            </button>
          </div>

          <!-- Inventory Alert Frequency -->
          <div
            v-if="alertFrequencies.length > 0"
            class="mt-6 pt-6 border-t border-gray-100"
          >
            <h3 class="text-sm font-semibold text-gray-800 mb-1">
              Inventory Alert Frequency
            </h3>
            <p class="text-xs text-gray-400 mb-4 leading-relaxed">
              Choose how often you receive inventory alert emails (low stock,
              expiring items) for each store.
            </p>
            <div class="space-y-3">
              <div
                v-for="af in alertFrequencies"
                :key="af.store_id"
                class="flex items-center justify-between"
              >
                <span class="text-sm text-gray-700 truncate flex-1 pr-3">{{
                  af.store_name
                }}</span>
                <select
                  :value="af.frequency"
                  @change="
                    updateAlertFrequency(
                      af.store_id,
                      $event.target.value,
                    )
                  "
                  class="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  <option value="DAILY">Daily</option>
                  <option value="WEEKLY">Weekly (Mondays)</option>
                  <option value="MONTHLY">Monthly (1st)</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div
          class="px-7 py-4 bg-gray-50 border-t border-gray-100 text-center text-xs text-gray-400"
        >
          <p>
            These settings only affect email notifications. Push notifications
            can be managed in the HovaStore app.
          </p>
          <p class="mt-1">
            &copy; HovaStore &middot;
            <a href="mailto:info@hova.ai" class="text-primary hover:underline"
              >info@hova.ai</a
            >
          </p>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <transition
      enter-active-class="transition ease-out duration-300"
      enter-from-class="opacity-0 translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition ease-in duration-200"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-2"
    >
      <div
        v-if="toastVisible"
        :class="[
          'fixed bottom-6 left-1/2 -translate-x-1/2 px-5 py-3 rounded-lg text-white text-sm shadow-lg z-50',
          toastError ? 'bg-red-600' : 'bg-gray-800',
        ]"
      >
        {{ toastMessage }}
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import http from "@/helper/http";

export default {
  name: "EmailPreferencesView",
  setup() {
    const route = useRoute();

    const isLoading = ref(true);
    const errorState = ref(false);
    const errorTitle = ref("");
    const errorMessage = ref("");
    const userName = ref("there");
    const groups = ref([]);
    const alertFrequencies = ref([]);
    const token = ref("");

    const toastVisible = ref(false);
    const toastMessage = ref("");
    const toastError = ref(false);
    let toastTimer = null;
    let debounceTimer = null;
    let pendingUpdates = {};

    const showToast = (msg, isError = false) => {
      clearTimeout(toastTimer);
      toastMessage.value = msg;
      toastError.value = isError;
      toastVisible.value = true;
      toastTimer = setTimeout(() => {
        toastVisible.value = false;
      }, 2500);
    };

    const setError = (title, message) => {
      errorState.value = true;
      errorTitle.value = title;
      errorMessage.value = message;
    };

    const fetchPreferences = async () => {
      try {
        isLoading.value = true;
        const response = await http.get(
          `/v2/notifications/email-preferences?token=${encodeURIComponent(
            token.value,
          )}`,
          {
            headers: {
              Accept: "application/json",
            },
          },
        );

        userName.value = response.data.user_name || "there";
        groups.value = response.data.groups || [];
        alertFrequencies.value = response.data.alert_frequencies || [];
      } catch (error) {
        const status = error.response?.status;
        const data = error.response?.data;

        if (status === 400) {
          setError(
            "Missing token",
            "No preferences token was provided in the URL.",
          );
        } else if (status === 403) {
          setError(
            "Invalid or expired link",
            "This preferences link has expired or is invalid. Please use the link from your most recent email.",
          );
        } else if (status === 404) {
          setError(
            "User not found",
            data?.error || "We couldn't find your account.",
          );
        } else {
          setError(
            "Something went wrong",
            "We couldn't load your preferences. Please try again later.",
          );
        }
      } finally {
        isLoading.value = false;
      }
    };

    const sendUpdates = async (updates) => {
      try {
        await http.post("/v2/notifications/email-preferences", {
          token: token.value,
          preferences: updates,
        });
        showToast("Preferences saved ✓");
      } catch {
        showToast("Failed to save — please try again", true);
      }
    };

    const togglePreference = (key, enabled) => {
      // Update local state immediately
      const group = groups.value.find((g) => g.key === key);
      if (group) group.enabled = enabled;
      checkAllDisabled();

      // Debounce API call
      pendingUpdates[key] = enabled;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const updates = { ...pendingUpdates };
        pendingUpdates = {};
        sendUpdates(updates);
      }, 400);
    };

    const allDisabled = ref(false);

    const checkAllDisabled = () => {
      allDisabled.value =
        groups.value.length > 0 && groups.value.every((g) => !g.enabled);
    };

    const unsubscribeAll = () => {
      if (
        !confirm(
          "Are you sure you want to unsubscribe from all email notifications?",
        )
      )
        return;

      const updates = {};
      groups.value.forEach((g) => {
        g.enabled = false;
        updates[g.key] = false;
      });
      sendUpdates(updates);
      checkAllDisabled();
    };

    const resubscribeAll = () => {
      const updates = {};
      groups.value.forEach((g) => {
        g.enabled = true;
        updates[g.key] = true;
      });
      sendUpdates(updates);
      checkAllDisabled();
    };

    const updateAlertFrequency = async (storeId, frequency) => {
      // Update local state immediately
      const af = alertFrequencies.value.find((a) => a.store_id === storeId);
      if (af) af.frequency = frequency;

      try {
        await http.post("/v2/notifications/alert-frequency", {
          token: token.value,
          store_id: storeId,
          frequency,
        });
        showToast("Alert frequency updated \u2713");
      } catch {
        showToast("Failed to save \u2014 please try again", true);
      }
    };

    onMounted(() => {
      token.value = route.query.token || "";

      if (!token.value) {
        setError(
          "Missing token",
          "No preferences token was provided in the URL.",
        );
        isLoading.value = false;
        return;
      }

      fetchPreferences().then(() => checkAllDisabled());
    });

    return {
      isLoading,
      errorState,
      errorTitle,
      errorMessage,
      userName,
      groups,
      alertFrequencies,
      allDisabled,
      toastVisible,
      toastMessage,
      toastError,
      togglePreference,
      unsubscribeAll,
      resubscribeAll,
      updateAlertFrequency,
    };
  },
};
</script>
